/**
 * Team Run–only tenor sort. Keeps Pricer / Feed / Trader on the shared
 * tenorComparator; this fixes Dec-26 ordering after mass archive.
 */

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const SEASONS = ["Sum", "Win"];

const PERIODS = {
  Months: { priority: 1 },
  Quarters: { priority: 2 },
  Seasons: { priority: 3 },
  Calendars: { priority: 4 },
};

const normalizeTenorToken = (value) => {
  if (value == null) return "";
  return String(value)
    .trim()
    .replace(/[\u2010-\u2015\u2212]/g, "-");
};

const getTenorValueFromString = (value) => {
  let formattedValue = normalizeTenorToken(value);
  const values = formattedValue.split(" ");
  if (values.length > 0) {
    formattedValue = values[0].trim();
  }
  return formattedValue;
};

const canonicalizePeriodLabel = (period) => {
  if (!period) return period;
  const lower = String(period).toLowerCase();
  const quarter = QUARTERS.find((item) => item.toLowerCase() === lower);
  if (quarter) return quarter;
  const month = MONTHS.find((item) => item.toLowerCase() === lower);
  if (month) return month;
  const season = SEASONS.find((item) => item.toLowerCase() === lower);
  if (season) return season;
  if (lower.startsWith("cal")) return "Cal";
  return period;
};

const getPeriodType = (value) => {
  const normalized = normalizeTenorToken(value);
  if (normalized.includes(",")) return "Months";
  const period = canonicalizePeriodLabel(normalized.split("-")[0]);
  if (QUARTERS.includes(period)) return "Quarters";
  if (MONTHS.includes(period)) return "Months";
  if (SEASONS.includes(period)) return "Seasons";
  if (String(period).toLowerCase().includes("cal")) return "Calendars";
  return "error";
};

const getPeriodTypeValue = (value) =>
  canonicalizePeriodLabel(normalizeTenorToken(value).split("-")[0]);

export const normalizeTenorYear = (yearStr) => {
  if (yearStr == null || yearStr === "") return null;
  const n = parseInt(String(yearStr).trim(), 10);
  if (Number.isNaN(n)) return null;
  return n < 100 ? 2000 + n : n;
};

const periodIndex = (list, value) =>
  list.indexOf(canonicalizePeriodLabel(value));

const compareCondition = (valueA, valueB) =>
  valueA === valueB ? 0 : valueA > valueB ? 1 : -1;

const getCalendarMonth = (value) => {
  const valueFormatted = normalizeTenorToken(value).split(" ");
  if (valueFormatted.length > 1) {
    return valueFormatted[1].replace("(", "").replace(")", "");
  }
  return null;
};

/**
 * AG Grid comparator for Team Run tenor column only.
 */
export const teamRunTenorComparator = (
  valueA,
  valueB,
  nodeA,
  nodeB,
  _isDescending
) => {
  valueA = valueA ?? "";
  valueB = valueB ?? "";

  const nodeAData = nodeA?.group ? nodeA.aggData : nodeA?.data;
  const nodeBData = nodeB?.group ? nodeB.aggData : nodeB?.data;
  if (
    nodeAData?.summary_id != null &&
    nodeAData.summary_id === nodeBData?.summary_id
  ) {
    return 0;
  }

  const formattedValueA = String(valueA).split("/")[0].trim();
  const formattedValueB = String(valueB).split("/")[0].trim();

  const periodStringA = getTenorValueFromString(formattedValueA);
  const periodStringB = getTenorValueFromString(formattedValueB);

  const periodTypeA = getPeriodType(periodStringA);
  const periodTypeB = getPeriodType(periodStringB);
  const periodValueA = getPeriodTypeValue(periodStringA);
  const periodValueB = getPeriodTypeValue(periodStringB);

  if (periodTypeA === "error" || periodTypeB === "error") {
    if (periodTypeA === "error" && periodTypeB === "error") {
      return compareCondition(periodStringA, periodStringB);
    }
    return periodTypeA === "error" ? 1 : -1;
  }

  if (periodTypeA !== periodTypeB) {
    return compareCondition(
      PERIODS[periodTypeA].priority,
      PERIODS[periodTypeB].priority
    );
  }

  const yearA = normalizeTenorYear(periodStringA.split("-")[1]);
  const yearB = normalizeTenorYear(periodStringB.split("-")[1]);
  if (yearA !== yearB) {
    if (yearA == null) return 1;
    if (yearB == null) return -1;
    return compareCondition(yearA, yearB);
  }

  if (periodValueA !== periodValueB) {
    if (periodTypeA === "Months") {
      return compareCondition(
        periodIndex(MONTHS, periodValueA),
        periodIndex(MONTHS, periodValueB)
      );
    }
    if (periodTypeA === "Seasons") {
      return compareCondition(
        periodIndex(SEASONS, periodValueA),
        periodIndex(SEASONS, periodValueB)
      );
    }
    if (periodTypeA === "Quarters") {
      return compareCondition(
        periodIndex(QUARTERS, periodValueA),
        periodIndex(QUARTERS, periodValueB)
      );
    }
  }

  let calendarMonthA = getCalendarMonth(valueA);
  let calendarMonthB = getCalendarMonth(valueB);

  if (periodTypeA === "Months") {
    return compareCondition(
      periodIndex(MONTHS, calendarMonthA),
      periodIndex(MONTHS, calendarMonthB)
    );
  }

  if (periodTypeA === "Calendars") {
    if (calendarMonthA == null) calendarMonthA = "Dec";
    if (calendarMonthB == null) calendarMonthB = "Dec";
    return compareCondition(
      periodIndex(MONTHS, calendarMonthA),
      periodIndex(MONTHS, calendarMonthB)
    );
  }

  return compareCondition(String(valueA), String(valueB));
};
